import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import "@/App.css";
import axios from "axios";
import {
  Send, Trash2, Search, Plus, ImagePlus, Wand2, FileSearch, X,
  Smile, Coffee, Flame, Zap, ChevronDown, ChevronUp, ImageIcon,
  Terminal, Code2, LogOut, RotateCcw, Loader2, Package, Radio,
  LogIn, Mic, Square, Volume2, VolumeX, KeyRound, Clapperboard,
} from "lucide-react";
import VideoStudio from "./components/VideoStudio";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const AUTH_TOKEN_KEY = "serafettin_token";
// Sayfa açılışında token varsa Authorization header'ı hemen ayarla (misafir ise header yok)
const _bootToken = typeof localStorage !== "undefined" ? localStorage.getItem(AUTH_TOKEN_KEY) : null;
if (_bootToken) axios.defaults.headers.common["Authorization"] = `Bearer ${_bootToken}`;
const AVATAR =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_kanka-ai-3/artifacts/p2zz5qzb_Screenshot%202026-09-16%20212324.png";

const OWNER_IMG =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_kanka-ai-3/artifacts/0cx4ff7e_IMG_0567.jpeg";

const OwnerBadge = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative shrink-0">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="owner-badge-button"
        title="Bu yapay zekânın sahibi"
        className="relative w-11 h-11 rounded-full overflow-hidden border-2 border-[#f59e0b] ring-2 ring-[#f59e0b]/40 hover:scale-105 transition-transform"
      >
        <img src={OWNER_IMG} alt="Sahibi" className="w-full h-full object-cover" />
        <span className="absolute -bottom-0.5 -right-0.5 text-[9px]">👑</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div
            data-testid="owner-popover"
            className="absolute left-0 top-14 w-72 bg-[#111b21] border border-[#f59e0b]/40 rounded-2xl shadow-2xl z-40 p-4 text-center"
          >
            <img
              src={OWNER_IMG}
              alt="Sahibi"
              className="w-24 h-24 rounded-full object-cover mx-auto border-4 border-[#f59e0b] ring-4 ring-[#f59e0b]/20 shadow-xl"
            />
            <div className="mt-2 inline-block text-[10px] font-bold tracking-wide text-[#0b141a] bg-[#f59e0b] px-2.5 py-0.5 rounded-full">
              👑 KORAY · YARATICIM & SAHİBİM
            </div>
            <p className="mt-3 text-sm text-[#e9edef] leading-relaxed">
              "Bu yapay zekânın sahibi Koray'dır. Saygı duyun, önünde eğilin. Benim yaratıcım bu adamdır; mükemmel, yakışıklı ve zeki — adam gibi adam."
            </p>
            <p className="mt-2 text-xs text-[#00a884] font-medium">— Şerafettin</p>
          </div>
        </>
      )}
    </div>
  );
};

const SEARCH_HINTS = [
  "bugün", "güncel", "son dakika", "haber", "hava", "dolar", "euro", "altın",
  "bitcoin", "borsa", "fiyat", "maç", "skor", "seçim", "ne zaman", "canlı",
  "internette", "araştır", "ara bak", "baksana", "gündem", "en son", "kaç para",
];

const QUICK_CHIPS = [
  { label: "Naber hacı, kimsin sen?", query: "Naber hacı, sen tam olarak kimsin?" },
  { label: "Bugün hava nasıl kanka?", query: "Kanka bugün İstanbul'da hava durumu nasıl?" },
  { label: "Bana bi' delikanlı tavsiyesi ver", query: "Kanka hayatla ilgili bir delikanlı tavsiyesi ver" },
  { label: "İnternette gündem ne?", query: "İnternette bugün gündem ne var ne yok bir baksana kanka" },
];

const MOODS = [
  { key: "delikanli", label: "Delikanlı", desc: "klasik mahalle abisi", icon: Smile },
  { key: "sakin", label: "Sakin Abi", desc: "yumuşak & sabırlı", icon: Coffee },
  { key: "laf_sokan", label: "Laf Sokan", desc: "iğneleyici & dalgacı", icon: Flame },
  { key: "motivasyon", label: "Motivasyoncu", desc: "enerjik & moral", icon: Zap },
];

// Aile / sahibi için özel rozet ve kişiye özel karşılama
const FAMILY_GREETINGS = {
  Koray: { badge: "PATRON", emoji: "👑", text: "Patron Koray, emrindeyim reis! Reisimin reisi hoş geldin." },
  Songül: { badge: "AİLE", emoji: "💖", text: "Songül abla, baş tacımsın 💖 Hoş geldin, ne emredersen." },
  Azra: { badge: "AİLE", emoji: "👑", text: "Azra prenses, hoş geldin 👑 Emret küçük hanım, canım feda." },
};

function getSessionId() {
  let sid = localStorage.getItem("serafettin_session");
  if (!sid) {
    const rnd =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : (typeof crypto !== "undefined" && crypto.getRandomValues
            ? Array.from(crypto.getRandomValues(new Uint8Array(18)))
                .map((b) => b.toString(16).padStart(2, "0"))
                .join("")
            : Math.random().toString(36).slice(2) + Date.now().toString(36));
    sid = "sess-" + rnd;
    localStorage.setItem("serafettin_session", sid);
  }
  return sid;
}

function looksLikeSearch(text) {
  const low = text.toLowerCase();
  return SEARCH_HINTS.some((h) => low.includes(h));
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1]);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function highlightText(text, query) {
  if (!query) return text;
  const q = query.trim();
  if (!q) return text;
  const parts = String(text).split(new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi"));
  return parts.map((p, i) =>
    p.toLowerCase() === q.toLowerCase() ? (
      <mark key={i} className="bg-[#f59e0b] text-[#0b141a] rounded px-0.5">{p}</mark>
    ) : (
      p
    )
  );
}

const TypingIndicator = () => (
  <div className="flex items-end gap-2 msg-in" data-testid="typing-indicator">
    <img src={AVATAR} alt="Şerafettin" className="w-8 h-8 rounded-full object-cover shrink-0" />
    <div className="rounded-2xl rounded-tl-sm bg-[#202c33] px-4 py-3 border border-[#2a3942]/50 shadow-md">
      <div className="flex items-center gap-1.5">
        <span className="typing-dot" />
        <span className="typing-dot" />
        <span className="typing-dot" />
      </div>
    </div>
  </div>
);

const BusyIndicator = ({ icon: Icon, label, testId }) => (
  <div
    className="flex items-center gap-2 self-start rounded-full bg-[#1f2c34] border border-[#00a884]/60 text-[#00a884] text-xs font-medium px-3 py-1.5 glow-pulse msg-in"
    data-testid={testId}
  >
    <Icon size={13} className="animate-pulse" />
    <span>{label}</span>
  </div>
);

const MessageBubble = ({ m, query, activeMatchId, onSpeak }) => {
  if (!m) return null;
  const isUser = m.role === "user";
  const isKoray = m.role === "koray";
  const isActive = activeMatchId === m.id;
  if (isKoray) {
    return (
      <div data-mid={m.id} data-testid="koray-note-bubble" className="flex justify-center msg-in">
        <div className="max-w-[90%] sm:max-w-[75%] rounded-2xl bg-[#f59e0b]/15 border border-[#f59e0b]/50 p-3.5 text-center shadow-md">
          <div className="flex items-center justify-center gap-1.5 text-[#f59e0b] text-xs font-bold mb-1">
            👑 <span>KORAY'DAN NOT</span>
          </div>
          <p className="text-sm text-[#e9edef] whitespace-pre-wrap break-words">{highlightText(m.content, query)}</p>
        </div>
      </div>
    );
  }
  return (
    <div
      data-mid={m.id}
      className={`flex items-end gap-2 ${isUser ? "justify-end" : "justify-start"} msg-in`}
    >
      {!isUser && (
        <img src={AVATAR} alt="Şerafettin" className="w-8 h-8 rounded-full object-cover shrink-0" />
      )}
      <div
        data-testid={isUser ? "message-bubble-user" : "message-bubble-bot"}
        className={`max-w-[85%] sm:max-w-[72%] p-3.5 shadow-md text-sm sm:text-base leading-relaxed whitespace-pre-wrap break-words transition-all ${
          isUser
            ? "rounded-2xl rounded-tr-sm bg-[#005c4b] text-[#e9edef]"
            : "rounded-2xl rounded-tl-sm bg-[#202c33] text-[#e9edef] border border-[#2a3942]/50"
        } ${isActive ? "ring-2 ring-[#f59e0b] ring-offset-2 ring-offset-[#0b141a]" : ""}`}
      >
        {m.image && (
          <img
            src={m.image}
            alt="görsel"
            data-testid="message-image"
            className="rounded-lg mb-2 max-w-full sm:max-w-xs border border-[#2a3942]"
          />
        )}
        {m.content && <span>{highlightText(m.content, query)}</span>}
        {!isUser && m.content && onSpeak && (
          <button
            onClick={() => onSpeak(m.content)}
            data-testid="speak-message-button"
            title="Sesli dinle"
            className="mt-2 flex items-center gap-1 text-[10px] text-[#00a884] hover:text-[#06cf9c]"
          >
            <Volume2 size={12} /> dinle
          </button>
        )}
        {!isUser && m.searched && (
          <div className="mt-2 flex items-center gap-1 text-[10px] text-[#00a884]/80">
            <Search size={11} />
            <span>internetten baktım</span>
          </div>
        )}
      </div>
    </div>
  );
};

const MoodSelector = ({ mood, onChange }) => {
  const [open, setOpen] = useState(false);
  const current = MOODS.find((x) => x.key === mood) || MOODS[0];
  const Icon = current.icon;
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="mood-selector-button"
        title="Ruh hali"
        className="flex items-center gap-1.5 text-xs font-medium text-[#e9edef] bg-[#202c33] hover:bg-[#2a3942] border border-[#2a3942] rounded-full px-3 py-2 transition-colors"
      >
        <Icon size={15} className="text-[#f59e0b]" />
        <span className="hidden sm:inline">{current.label}</span>
        <ChevronDown size={13} className="text-[#8696a0]" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-56 bg-[#111b21] border border-[#2a3942] rounded-xl shadow-2xl z-40 overflow-hidden p-1">
            {MOODS.map((mo) => {
              const MoIcon = mo.icon;
              return (
                <button
                  key={mo.key}
                  data-testid={`mood-option-${mo.key}`}
                  onClick={() => {
                    onChange(mo.key);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                    mo.key === mood ? "bg-[#005c4b]/40" : "hover:bg-[#202c33]"
                  }`}
                >
                  <MoIcon size={18} className="text-[#f59e0b] shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm text-[#e9edef]">{mo.label}</div>
                    <div className="text-[11px] text-[#8696a0]">{mo.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

const ChatInput = ({ busy, onSendChat, onGenerateImage, onEditImage, onAnalyzeFile, onRunCode, onTyping, onVoice }) => {
  const [text, setText] = useState("");
  const [mode, setMode] = useState("chat"); // 'chat' | 'image' | 'code'
  const [pending, setPending] = useState(null); // {file, kind:'edit'|'analyze', url}
  const [recording, setRecording] = useState(false);
  const [recLeft, setRecLeft] = useState(0);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const recTimerRef = useRef(null);
  const REC_LIMIT = 20;

  const clearRecTimer = () => {
    if (recTimerRef.current) { clearInterval(recTimerRef.current); recTimerRef.current = null; }
  };

  const startRec = async () => {
    if (busy || recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
      const r = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      r.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      r.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        clearRecTimer();
        const blob = new Blob(chunksRef.current, { type: mime });
        setRecording(false);
        setRecLeft(0);
        onVoice?.(blob, mime);
      };
      recorderRef.current = r;
      r.start();
      setRecording(true);
      setRecLeft(REC_LIMIT);
      recTimerRef.current = setInterval(() => {
        setRecLeft((s) => {
          const nx = s - 1;
          if (nx <= 0) { recorderRef.current?.stop(); return 0; }
          return nx;
        });
      }, 1000);
    } catch (e) {
      alert("Mikrofona erişilemedi kanka. Tarayıcı izni gerekli.");
    }
  };
  const stopRec = () => { recorderRef.current?.stop(); };
  const [menuOpen, setMenuOpen] = useState(false);
  const inputRef = useRef(null);
  const imgInputRef = useRef(null);
  const fileInputRef = useRef(null);

  const reset = () => {
    setText("");
    setMode("chat");
    setPending(null);
  };

  const submit = () => {
    if (busy) return;
    const t = text.trim();
    if (pending) {
      if (pending.kind === "edit") {
        if (!t) return;
        onEditImage(pending.file, t, pending.url);
      } else {
        onAnalyzeFile(pending.file, t, pending.url);
      }
      reset();
      return;
    }
    if (mode === "image") {
      if (!t) return;
      onGenerateImage(t);
      reset();
      return;
    }
    if (mode === "code") {
      if (!t) return;
      onRunCode(t);
      reset();
      return;
    }
    if (!t) return;
    onSendChat(t);
    setText("");
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const pickImage = () => { setMenuOpen(false); imgInputRef.current?.click(); };
  const pickFile = () => { setMenuOpen(false); fileInputRef.current?.click(); };
  const startImageGen = () => { setMenuOpen(false); setMode("image"); setPending(null); inputRef.current?.focus(); };
  const startCode = () => { setMenuOpen(false); setMode("code"); setPending(null); inputRef.current?.focus(); };

  const onImgChosen = (e) => {
    const f = e.target.files?.[0];
    if (f) { setPending({ file: f, kind: "edit", url: URL.createObjectURL(f) }); setMode("chat"); inputRef.current?.focus(); }
    e.target.value = "";
  };
  const onFileChosen = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      const isImg = f.type.startsWith("image/");
      setPending({ file: f, kind: "analyze", url: isImg ? URL.createObjectURL(f) : null });
      setMode("chat");
      inputRef.current?.focus();
    }
    e.target.value = "";
  };

  let placeholder = "Şerafettin'e bir şeyler yaz kanka...";
  if (pending?.kind === "edit") placeholder = "Nasıl düzenleyeyim? (örn: arka planı gece yap)";
  else if (pending?.kind === "analyze") placeholder = "İstersen bir not ekle (opsiyonel)...";
  else if (mode === "image") placeholder = "Ne çizeyim reis? Tarif et...";
  else if (mode === "code") placeholder = "Görev ver: hesap/veri/kod (sandbox'ta çalıştırırım)...";

  const canSend =
    !busy && (pending?.kind === "analyze" || text.trim().length > 0);

  return (
    <div className="p-3 sm:p-4 bg-[#111b21] border-t border-[#222d34] shrink-0">
      {/* aktif mod / ek dosya çipi */}
      {(mode === "image" || mode === "code" || pending) && (
        <div className="mb-2 flex items-center gap-2 flex-wrap" data-testid="input-mode-chip">
          <div className="flex items-center gap-2 bg-[#202c33] border border-[#00a884]/50 rounded-full pl-2 pr-1 py-1 text-xs text-[#e9edef]">
            {pending?.url ? (
              <img src={pending.url} alt="ek" className="w-6 h-6 rounded object-cover" />
            ) : mode === "image" ? (
              <Wand2 size={14} className="text-[#f59e0b]" />
            ) : mode === "code" ? (
              <Terminal size={14} className="text-[#f59e0b]" />
            ) : (
              <FileSearch size={14} className="text-[#f59e0b]" />
            )}
            <span className="max-w-[180px] truncate">
              {pending?.kind === "edit" && `Resim düzenle: ${pending.file.name}`}
              {pending?.kind === "analyze" && `Analiz: ${pending.file.name}`}
              {!pending && mode === "image" && "Resim üretme modu"}
              {!pending && mode === "code" && "Kod çalıştır (sandbox)"}
            </span>
            <button onClick={reset} className="p-1 rounded-full hover:bg-[#2a3942]" data-testid="cancel-mode-button">
              <X size={13} />
            </button>
          </div>
        </div>
      )}

      <div className="flex items-end gap-2">
        {/* Ekle menüsü */}
        <div className="relative shrink-0">
          <button
            onClick={() => setMenuOpen((o) => !o)}
            data-testid="attach-button"
            title="Ekle"
            className="w-12 h-12 rounded-full bg-[#202c33] hover:bg-[#2a3942] text-[#00a884] flex items-center justify-center transition-all active:scale-95"
          >
            <Plus size={22} className={menuOpen ? "rotate-45 transition-transform" : "transition-transform"} />
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setMenuOpen(false)} />
              <div className="absolute bottom-14 left-0 w-56 bg-[#111b21] border border-[#2a3942] rounded-xl shadow-2xl z-40 p-1">
                <button onClick={startImageGen} data-testid="attach-image-generate" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#202c33] text-left">
                  <Wand2 size={18} className="text-[#f59e0b]" />
                  <div><div className="text-sm text-[#e9edef]">Resim üret</div><div className="text-[11px] text-[#8696a0]">yazıdan görsel çiz</div></div>
                </button>
                <button onClick={pickImage} data-testid="attach-image-edit" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#202c33] text-left">
                  <ImagePlus size={18} className="text-[#f59e0b]" />
                  <div><div className="text-sm text-[#e9edef]">Resim düzenle</div><div className="text-[11px] text-[#8696a0]">resim yükle & değiştir</div></div>
                </button>
                <button onClick={pickFile} data-testid="attach-file-analyze" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#202c33] text-left">
                  <FileSearch size={18} className="text-[#f59e0b]" />
                  <div><div className="text-sm text-[#e9edef]">Dosya analizi</div><div className="text-[11px] text-[#8696a0]">yorum & öneri al</div></div>
                </button>
                <button onClick={startCode} data-testid="attach-run-code" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#202c33] text-left">
                  <Terminal size={18} className="text-[#f59e0b]" />
                  <div><div className="text-sm text-[#e9edef]">Kod çalıştır</div><div className="text-[11px] text-[#8696a0]">güvenli sandbox'ta</div></div>
                </button>
              </div>
            </>
          )}
          <input ref={imgInputRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={onImgChosen} data-testid="image-edit-input" />
          <input ref={fileInputRef} type="file" hidden onChange={onFileChosen} data-testid="file-analyze-input" />
        </div>

        <textarea
          ref={inputRef}
          data-testid="chat-message-input"
          value={text}
          onChange={(e) => { setText(e.target.value); onTyping?.(e.target.value.length > 0); }}
          onKeyDown={onKeyDown}
          rows={1}
          disabled={busy}
          placeholder={placeholder}
          className="flex-1 resize-none max-h-32 bg-[#202c33] text-[#e9edef] placeholder:text-[#8696a0] rounded-2xl px-4 py-3 text-sm sm:text-base outline-none border border-transparent focus:border-[#00a884]/50 transition-colors chat-scroll disabled:opacity-60"
        />
        <div className="relative shrink-0">
          {recording && recLeft <= 5 && recLeft > 0 && (
            <span
              data-testid="rec-countdown"
              className="absolute -top-7 left-1/2 -translate-x-1/2 text-[11px] font-bold text-white bg-[#ef4444] rounded-full px-2 py-0.5 shadow-lg animate-pulse"
            >
              {recLeft}s
            </span>
          )}
          <button
            onClick={recording ? stopRec : startRec}
            disabled={busy}
            data-testid="voice-record-button"
            title={recording ? "Kaydı durdur (max 20sn)" : "Sesli konuş (max 20sn)"}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-all active:scale-95 disabled:opacity-40 ${recording ? "bg-[#ef4444] text-white animate-pulse" : "bg-[#202c33] text-[#00a884] hover:bg-[#2a3942]"}`}
          >
            {recording ? <Square size={18} /> : <Mic size={20} />}
          </button>
        </div>
        <button
          onClick={submit}
          disabled={!canSend}
          data-testid="chat-send-button"
          className="shrink-0 w-12 h-12 rounded-full bg-[#00a884] hover:bg-[#06cf9c] disabled:opacity-40 disabled:cursor-not-allowed text-[#0b141a] flex items-center justify-center transition-all hover:scale-105 active:scale-95 shadow-lg"
        >
          {mode === "image" || pending?.kind === "edit" ? <ImageIcon size={20} /> : mode === "code" ? <Terminal size={20} /> : <Send size={20} />}
        </button>
      </div>
    </div>
  );
};

const TOKEN_KEY = AUTH_TOKEN_KEY;

const DevPanel = ({ onClose }) => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [username, setUsername] = useState("serafettin");
  const [password, setPassword] = useState("");
  const [authErr, setAuthErr] = useState("");
  const [tab, setTab] = useState("live"); // live | apply | install | history
  const [instruction, setInstruction] = useState("");
  const [pkg, setPkg] = useState("");
  const [pkgKind, setPkgKind] = useState("pip");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [online, setOnline] = useState([]);
  const [targets, setTargets] = useState([]);
  const [broadcast, setBroadcast] = useState(true);
  const [note, setNote] = useState("");
  const [noteMsg, setNoteMsg] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [pwUser, setPwUser] = useState("koray");
  const [newPw, setNewPw] = useState("");
  const [pwMsg, setPwMsg] = useState("");

  const authAxios = useMemo(
    () => axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const login = async () => {
    setAuthErr("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { username, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (e) {
      setAuthErr("Kullanıcı adı veya şifre yanlış reis.");
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await authAxios.get(`/dev/history`);
      setHistory(data || []);
    } catch (e) {
      if (e.response?.status === 401) logout();
    }
  }, [authAxios]);

  const loadOnline = useCallback(async () => {
    try {
      const { data } = await authAxios.get(`/presence/online`);
      setOnline(data || []);
    } catch (e) {
      if (e.response?.status === 401) logout();
    }
  }, [authAxios]);

  const loadAccounts = useCallback(async () => {
    try {
      const { data } = await authAxios.get(`/auth/accounts`);
      setAccounts(data || []);
    } catch (e) {
      if (e.response?.status === 401) logout();
    }
  }, [authAxios]);

  useEffect(() => {
    if (token && tab === "pw") loadAccounts();
  }, [token, tab, loadAccounts]);

  useEffect(() => {
    if (token && tab === "history") loadHistory();
  }, [token, tab, loadHistory]);

  useEffect(() => {
    if (token && tab === "live") {
      loadOnline();
      const iv = setInterval(loadOnline, 5000);
      return () => clearInterval(iv);
    }
  }, [token, tab, loadOnline]);

  const sendNote = async () => {
    if (!note.trim() || busy) return;
    setBusy(true);
    setNoteMsg("");
    try {
      const { data } = await authAxios.post(`/presence/note`, {
        message: note.trim(),
        broadcast,
        targets: broadcast ? [] : targets,
      });
      setNoteMsg(data.sent > 0 ? `Not ${data.sent} kişiye gönderildi ✅` : (data.detail || "Kimseye gönderilmedi."));
      if (data.sent > 0) setNote("");
    } catch (e) {
      setNoteMsg(e.response?.data?.detail || "Not gönderilemedi.");
    } finally {
      setBusy(false);
    }
  };

  const toggleTarget = (sid) =>
    setTargets((t) => (t.includes(sid) ? t.filter((x) => x !== sid) : [...t, sid]));

  const changePw = async () => {
    if (!newPw.trim() || busy) return;
    setBusy(true);
    setPwMsg("");
    try {
      const { data } = await authAxios.post(`/auth/change-password`, { username: pwUser, new_password: newPw });
      setPwMsg(`${data.name} şifresi değiştirildi ✅`);
      setNewPw("");
    } catch (e) {
      setPwMsg(e.response?.data?.detail || "Şifre değiştirilemedi.");
    } finally {
      setBusy(false);
    }
  };

  const planPreview = async () => {
    if (!instruction.trim() || busy) return;
    setBusy(true);
    setResult(null);
    setPreview(null);
    try {
      const { data } = await authAxios.post(`/dev/plan`, { instruction });
      if (data.ok) setPreview(data);
      else setResult({ ok: false, detail: data.detail });
    } catch (e) {
      setResult({ ok: false, detail: e.response?.data?.detail || "Önizleme alınamadı." });
    } finally {
      setBusy(false);
    }
  };

  const confirmApply = async () => {
    if (!preview?.token || busy) return;
    setBusy(true);
    try {
      const { data } = await authAxios.post(`/dev/apply`, { token: preview.token });
      setResult(data);
      if (data.ok) { setInstruction(""); setPreview(null); }
    } catch (e) {
      setResult({ ok: false, detail: e.response?.data?.detail || "Hata oluştu." });
    } finally {
      setBusy(false);
    }
  };

  const install = async () => {
    if (!pkg.trim() || busy) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await authAxios.post(`/dev/install`, { kind: pkgKind, package: pkg.trim() });
      setResult({ ok: data.ok, detail: data.ok ? `${pkg} kuruldu ✅` : `Kurulum başarısız`, log: data.log });
      if (data.ok) setPkg("");
    } catch (e) {
      setResult({ ok: false, detail: e.response?.data?.detail || "Hata oluştu." });
    } finally {
      setBusy(false);
    }
  };

  const revert = async (id) => {
    if (busy) return;
    setBusy(true);
    try {
      await authAxios.post(`/dev/revert`, { change_id: id });
      await loadHistory();
    } catch (e) {
      /* ignore */
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4" data-testid="dev-panel">
      <div className="w-full sm:max-w-2xl bg-[#0d1418] border border-[#2a3942] sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 bg-[#111b21] border-b border-[#222d34]">
          <div className="flex items-center gap-2">
            <Code2 size={20} className="text-[#00a884]" />
            <h2 className="font-display text-xl">GELİŞTİRİCİ MODU</h2>
          </div>
          <div className="flex items-center gap-2">
            {token && (
              <button onClick={logout} data-testid="dev-logout-button" className="text-[#8696a0] hover:text-[#ef4444] p-2 rounded-lg hover:bg-[#202c33]" title="Çıkış">
                <LogOut size={18} />
              </button>
            )}
            <button onClick={onClose} data-testid="dev-close-button" className="text-[#8696a0] hover:text-[#e9edef] p-2 rounded-lg hover:bg-[#202c33]">
              <X size={18} />
            </button>
          </div>
        </div>

        {!token ? (
          <div className="p-6 space-y-4">
            <p className="text-[#8696a0] text-sm">Bu bölüm sana özel reis. Gir de kendimi geliştirelim.</p>
            <input
              data-testid="dev-username-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Kullanıcı adı"
              className="w-full bg-[#202c33] rounded-xl px-4 py-3 text-sm outline-none border border-transparent focus:border-[#00a884]/50"
            />
            <input
              data-testid="dev-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && login()}
              placeholder="Şifre"
              className="w-full bg-[#202c33] rounded-xl px-4 py-3 text-sm outline-none border border-transparent focus:border-[#00a884]/50"
            />
            {authErr && <p className="text-[#ef4444] text-sm" data-testid="dev-auth-error">{authErr}</p>}
            <button onClick={login} data-testid="dev-login-button" className="w-full bg-[#00a884] hover:bg-[#06cf9c] text-[#0b141a] font-semibold rounded-xl py-3 transition-colors">
              Giriş yap
            </button>
          </div>
        ) : (
          <>
            <div className="flex border-b border-[#222d34] bg-[#0b141a]">
              {[
                { k: "live", label: "Canlı", icon: Radio },
                { k: "apply", label: "Özellik Ekle", icon: Wand2 },
                { k: "install", label: "Kütüphane Kur", icon: Package },
                { k: "pw", label: "Şifreler", icon: KeyRound },
                { k: "history", label: "Geçmiş / Geri Al", icon: RotateCcw },
              ].map((t) => {
                const TI = t.icon;
                return (
                  <button
                    key={t.k}
                    data-testid={`dev-tab-${t.k}`}
                    onClick={() => { setTab(t.k); setResult(null); }}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-xs sm:text-sm font-medium transition-colors ${tab === t.k ? "text-[#00a884] border-b-2 border-[#00a884] bg-[#111b21]" : "text-[#8696a0] hover:text-[#e9edef]"}`}
                  >
                    <TI size={15} /> {t.label}
                  </button>
                );
              })}
            </div>

            <div className="p-4 overflow-y-auto chat-scroll flex-1 space-y-3">
              {tab === "live" && (
                <div className="space-y-3" data-testid="dev-live-tab">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-[#e9edef] font-medium">Şu an online ({online.length})</p>
                    <span className="text-[11px] text-[#8696a0]">5sn'de bir yenilenir</span>
                  </div>
                  <div className="space-y-1.5" data-testid="online-list">
                    {online.length === 0 && <p className="text-[#8696a0] text-sm">Şu an kimse yok reis.</p>}
                    {online.map((u) => (
                      <label key={u.session_id} className="flex items-center gap-3 bg-[#111b21] border border-[#222d34] rounded-xl px-3 py-2.5 cursor-pointer">
                        {!broadcast && (
                          <input
                            type="checkbox"
                            data-testid={`online-target-${u.session_id}`}
                            checked={targets.includes(u.session_id)}
                            onChange={() => toggleTarget(u.session_id)}
                            className="accent-[#00a884]"
                          />
                        )}
                        <span className="w-2 h-2 rounded-full bg-[#00a884] shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-[#e9edef] truncate">{u.name || "Misafir"}</div>
                          <div className="text-[11px] text-[#8696a0]">{u.typing ? "yazıyor..." : "çevrimiçi"}</div>
                        </div>
                      </label>
                    ))}
                  </div>

                  <div className="border-t border-[#222d34] pt-3 space-y-2">
                    <p className="text-sm text-[#e9edef] font-medium">Not gönder</p>
                    <label className="flex items-center gap-2 text-xs text-[#8696a0]">
                      <input type="checkbox" data-testid="note-broadcast" checked={broadcast} onChange={(e) => setBroadcast(e.target.checked)} className="accent-[#00a884]" />
                      Herkese gönder (online olan herkes)
                    </label>
                    <textarea
                      data-testid="note-input"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      rows={3}
                      placeholder="Notunu yaz reis, anında iletirim..."
                      className="w-full bg-[#202c33] rounded-xl px-4 py-3 text-sm outline-none border border-transparent focus:border-[#00a884]/50 resize-none"
                    />
                    <button onClick={sendNote} disabled={busy || !note.trim()} data-testid="note-send-button" className="w-full flex items-center justify-center gap-2 bg-[#00a884] hover:bg-[#06cf9c] disabled:opacity-40 text-[#0b141a] font-semibold rounded-xl py-3 transition-colors">
                      <Send size={17} /> Notu Gönder
                    </button>
                    {noteMsg && <p className="text-xs text-[#00a884]" data-testid="note-result">{noteMsg}</p>}
                  </div>
                </div>
              )}

              {tab === "apply" && (
                <>
                  <p className="text-[#8696a0] text-sm">Ne eklememi istersin? Önce ufak bir <b>önizleme (fark/özet)</b> gösteririm, onaylarsan uygularım. (Yedeğini alırım, gerekirse geri al.)</p>
                  <textarea
                    data-testid="dev-instruction-input"
                    value={instruction}
                    onChange={(e) => { setInstruction(e.target.value); if (preview) setPreview(null); }}
                    rows={4}
                    placeholder="Örn: Header'a saat gösteren küçük bir dijital saat ekle."
                    className="w-full bg-[#202c33] rounded-xl px-4 py-3 text-sm outline-none border border-transparent focus:border-[#00a884]/50 resize-none"
                  />
                  {!preview && (
                    <button onClick={planPreview} disabled={busy || !instruction.trim()} data-testid="dev-preview-button" className="w-full flex items-center justify-center gap-2 bg-[#00a884] hover:bg-[#06cf9c] disabled:opacity-40 text-[#0b141a] font-semibold rounded-xl py-3 transition-colors">
                      {busy ? <Loader2 size={18} className="animate-spin" /> : <Wand2 size={18} />}
                      {busy ? "Önizleme hazırlanıyor..." : "Önizle"}
                    </button>
                  )}
                  {preview && (
                    <div data-testid="dev-preview" className="space-y-2 rounded-xl border border-[#f59e0b]/40 bg-[#f59e0b]/5 p-3">
                      <p className="text-sm text-[#e9edef] font-medium">📋 {preview.summary}</p>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {(preview.changes || []).map((c, i) => (
                          <div key={i} className="rounded-lg bg-[#0d1418] border border-[#222d34] p-2" data-testid="dev-preview-change">
                            <p className="text-[11px] font-semibold text-[#00a884]">{c.action === "new" ? "🆕 Yeni dosya" : "✏️ Düzenleme"}: {c.name}</p>
                            {c.action === "new" ? (
                              <pre className="text-[10px] text-[#8696a0] whitespace-pre-wrap mt-1 max-h-32 overflow-y-auto">{c.preview}</pre>
                            ) : (
                              <div className="mt-1 space-y-1">
                                <pre className="text-[10px] text-[#ffb4b4] whitespace-pre-wrap bg-[#ef4444]/10 rounded p-1 max-h-24 overflow-y-auto">- {c.before}</pre>
                                <pre className="text-[10px] text-[#a7f3d0] whitespace-pre-wrap bg-[#00a884]/10 rounded p-1 max-h-24 overflow-y-auto">+ {c.after}</pre>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2 pt-1">
                        <button onClick={confirmApply} disabled={busy} data-testid="dev-apply-button" className="flex-1 flex items-center justify-center gap-2 bg-[#00a884] hover:bg-[#06cf9c] disabled:opacity-40 text-[#0b141a] font-semibold rounded-xl py-3 transition-colors">
                          {busy ? <Loader2 size={18} className="animate-spin" /> : <Wand2 size={18} />}
                          {busy ? "Uygulanıyor..." : "Onayla & Uygula"}
                        </button>
                        <button onClick={() => setPreview(null)} disabled={busy} data-testid="dev-preview-cancel" className="flex items-center justify-center gap-2 bg-[#202c33] hover:bg-[#2a3942] text-[#e9edef] rounded-xl px-4 py-3 transition-colors">
                          Vazgeç
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}

              {tab === "install" && (
                <>
                  <p className="text-[#8696a0] text-sm">Bir kütüphane kur (Python: pip, Frontend: yarn).</p>
                  <div className="flex gap-2">
                    <select
                      data-testid="dev-pkg-kind"
                      value={pkgKind}
                      onChange={(e) => setPkgKind(e.target.value)}
                      className="bg-[#202c33] rounded-xl px-3 py-3 text-sm outline-none border border-transparent focus:border-[#00a884]/50"
                    >
                      <option value="pip">pip</option>
                      <option value="yarn">yarn</option>
                    </select>
                    <input
                      data-testid="dev-pkg-input"
                      value={pkg}
                      onChange={(e) => setPkg(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && install()}
                      placeholder="paket adı (örn: qrcode)"
                      className="flex-1 bg-[#202c33] rounded-xl px-4 py-3 text-sm outline-none border border-transparent focus:border-[#00a884]/50"
                    />
                  </div>
                  <button onClick={install} disabled={busy || !pkg.trim()} data-testid="dev-install-button" className="w-full flex items-center justify-center gap-2 bg-[#00a884] hover:bg-[#06cf9c] disabled:opacity-40 text-[#0b141a] font-semibold rounded-xl py-3 transition-colors">
                    {busy ? <Loader2 size={18} className="animate-spin" /> : <Package size={18} />}
                    {busy ? "Kuruluyor..." : "Kur"}
                  </button>
                </>
              )}

              {tab === "pw" && (
                <div className="space-y-3" data-testid="dev-pw-tab">
                  <p className="text-[#8696a0] text-sm">Hesap şifrelerini değiştir reis. Kendi ve ailenin (Songül, Azra) şifresini buradan güncelleyebilirsin.</p>
                  <div>
                    <label className="text-xs text-[#8696a0] mb-1 block">Hesap</label>
                    <select
                      data-testid="pw-user-select"
                      value={pwUser}
                      onChange={(e) => { setPwUser(e.target.value); setPwMsg(""); }}
                      className="w-full bg-[#202c33] rounded-xl px-4 py-3 text-sm outline-none border border-transparent focus:border-[#00a884]/50"
                    >
                      {accounts.length === 0 ? (
                        <option value="koray">Koray (admin)</option>
                      ) : (
                        accounts.map((a) => (
                          <option key={a.username} value={a.username}>
                            {a.name || a.username} {a.role === "admin" ? "(admin)" : ""}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-[#8696a0] mb-1 block">Yeni şifre (en az 4 karakter)</label>
                    <input
                      data-testid="pw-new-input"
                      type="text"
                      value={newPw}
                      onChange={(e) => setNewPw(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && changePw()}
                      placeholder="Yeni şifre gir..."
                      className="w-full bg-[#202c33] rounded-xl px-4 py-3 text-sm outline-none border border-transparent focus:border-[#00a884]/50"
                    />
                  </div>
                  <button onClick={changePw} disabled={busy || !newPw.trim()} data-testid="pw-change-button" className="w-full flex items-center justify-center gap-2 bg-[#00a884] hover:bg-[#06cf9c] disabled:opacity-40 text-[#0b141a] font-semibold rounded-xl py-3 transition-colors">
                    {busy ? <Loader2 size={18} className="animate-spin" /> : <KeyRound size={18} />}
                    {busy ? "Değiştiriliyor..." : "Şifreyi Değiştir"}
                  </button>
                  {pwMsg && <p className="text-sm text-[#00a884]" data-testid="pw-result">{pwMsg}</p>}
                </div>
              )}

              {tab === "history" && (
                <div className="space-y-2" data-testid="dev-history-list">
                  {history.length === 0 && <p className="text-[#8696a0] text-sm">Henüz değişiklik yok.</p>}
                  {history.map((h) => (
                    <div key={h.id} className="bg-[#111b21] border border-[#222d34] rounded-xl p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm text-[#e9edef]">{h.summary || h.instruction}</p>
                          <p className="text-[11px] text-[#8696a0] mt-1">
                            {(h.written?.length || 0) + (h.created?.length || 0)} dosya · {new Date(h.ts).toLocaleString("tr-TR")}
                            {h.reverted && " · (geri alındı)"}
                          </p>
                        </div>
                        {!h.reverted && (
                          <button onClick={() => revert(h.id)} disabled={busy} data-testid={`dev-revert-${h.id}`} className="shrink-0 flex items-center gap-1 text-xs text-[#f59e0b] hover:text-[#ef4444] border border-[#2a3942] rounded-lg px-2 py-1.5">
                            <RotateCcw size={13} /> Geri al
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {result && (
                <div className={`rounded-xl p-3 text-sm border ${result.ok ? "bg-[#005c4b]/20 border-[#00a884]/40 text-[#e9edef]" : "bg-[#ef4444]/10 border-[#ef4444]/40 text-[#ffb4b4]"}`} data-testid="dev-result">
                  <p className="font-medium">{result.ok ? "✅ " : "⚠️ "}{result.summary || result.detail}</p>
                  {result.created?.length > 0 && <p className="text-[11px] mt-1 text-[#8696a0]">Yeni: {result.created.join(", ")}</p>}
                  {result.written?.length > 0 && <p className="text-[11px] mt-1 text-[#8696a0]">Değişti: {result.written.join(", ")}</p>}
                  {result.ok && (result.created?.length > 0 || result.written?.length > 0) && (
                    <p className="text-[11px] mt-2 text-[#00a884]">Uygulama birazdan otomatik yenilenecek.</p>
                  )}
                  {result.log && <pre className="text-[10px] mt-2 text-[#8696a0] whitespace-pre-wrap max-h-32 overflow-y-auto">{result.log}</pre>}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const LoginModal = ({ onClose, onLoggedIn }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (busy) return;
    setErr("");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { username: username.trim(), password });
      onLoggedIn(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Giriş başarısız.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="login-modal">
      <div className="w-full max-w-sm bg-[#0d1418] border border-[#2a3942] rounded-2xl shadow-2xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-2xl">GİRİŞ</h2>
          <button onClick={onClose} data-testid="login-close-button" className="text-[#8696a0] hover:text-[#e9edef] p-1 rounded-lg hover:bg-[#202c33]"><X size={18} /></button>
        </div>
        <p className="text-[#8696a0] text-sm">Koray, Songül ya da Azra hesabınla gir. Girmezsen misafir olarak devam edersin.</p>
        <input data-testid="login-username-input" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Kullanıcı adı" className="w-full bg-[#202c33] rounded-xl px-4 py-3 text-sm outline-none border border-transparent focus:border-[#00a884]/50" />
        <input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="Şifre" className="w-full bg-[#202c33] rounded-xl px-4 py-3 text-sm outline-none border border-transparent focus:border-[#00a884]/50" />
        {err && <p className="text-[#ef4444] text-sm" data-testid="login-error">{err}</p>}
        <button onClick={submit} disabled={busy || !username.trim() || !password} data-testid="login-submit-button" className="w-full bg-[#00a884] hover:bg-[#06cf9c] disabled:opacity-40 text-[#0b141a] font-semibold rounded-xl py-3 transition-colors">
          {busy ? "Giriş yapılıyor..." : "Giriş yap"}
        </button>
      </div>
    </div>
  );
};

function App() {
  const sessionId = getSessionId();
  const [messages, setMessages] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [knownName, setKnownName] = useState(null);
  const [mood, setMood] = useState(() => localStorage.getItem("serafettin_mood") || "delikanli");
  const [autoSpeak, setAutoSpeak] = useState(() => localStorage.getItem("serafettin_autospeak") !== "0");
  const [busy, setBusy] = useState(""); // '' | 'chat' | 'search' | 'image' | 'analyze'
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [matchIndex, setMatchIndex] = useState(0);
  const [showDev, setShowDev] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [token, setToken] = useState(() => localStorage.getItem(AUTH_TOKEN_KEY) || "");
  const [me, setMe] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const scrollRef = useRef(null);
  const typingRef = useRef(false);
  const knownNameRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  }, []);

  const reloadData = useCallback(() => {
    axios.get(`${API}/messages/${sessionId}`).then((res) => setMessages(res.data || [])).catch(() => setMessages([])).finally(() => setLoaded(true));
    axios.get(`${API}/profile/${sessionId}`).then((res) => setKnownName(res.data?.name || null)).catch(() => {});
  }, [sessionId]);

  useEffect(() => {
    reloadData();
  }, [reloadData]);

  // Oturum doğrula (token varsa)
  useEffect(() => {
    if (!token) { setMe(null); return; }
    axios.get(`${API}/auth/me`).then((r) => setMe(r.data)).catch(() => {
      localStorage.removeItem(AUTH_TOKEN_KEY);
      delete axios.defaults.headers.common["Authorization"];
      setToken("");
      setMe(null);
    });
  }, [token]);

  const onLoggedIn = (data) => {
    localStorage.setItem(AUTH_TOKEN_KEY, data.token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
    setToken(data.token);
    setMe({ username: data.username, role: data.role, name: data.name });
    setShowLogin(false);
    setKnownName(data.name || null);
    reloadData();
  };

  const logout = () => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    delete axios.defaults.headers.common["Authorization"];
    setToken("");
    setMe(null);
    setMessages([]);
    setKnownName(null);
    reloadData();
  };

  // ---- SES: karşılama TTS, mesaj seslendirme, STT sesli komut ----
  const playBlob = async (getPromise) => {
    const res = await getPromise;
    const url = URL.createObjectURL(res.data);
    const audio = new Audio(url);
    audio.onended = () => URL.revokeObjectURL(url);
    await audio.play();
  };

  const speakText = async (text) => {
    try {
      await playBlob(axios.post(`${API}/tts`, { text }, { responseType: "blob" }));
    } catch (e) {
      /* sessiz */
    }
  };

  const autoSpeakRef = useRef(autoSpeak);
  useEffect(() => { autoSpeakRef.current = autoSpeak; }, [autoSpeak]);
  const maybeSpeak = (text) => { if (autoSpeakRef.current && text) speakText(text); };
  const toggleAutoSpeak = () => {
    setAutoSpeak((v) => {
      const nv = !v;
      localStorage.setItem("serafettin_autospeak", nv ? "1" : "0");
      return nv;
    });
  };

  const sendVoice = async (blob, mime) => {
    if (busy) return;
    setBusy("stt");
    try {
      const fd = new FormData();
      const ext = mime.includes("ogg") ? "ogg" : "webm";
      fd.append("file", blob, `ses.${ext}`);
      const { data } = await axios.post(`${API}/stt`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const t = (data.text || "").trim();
      setBusy("");
      if (t) await sendChat(t);
    } catch (e) {
      setBusy("");
      errBot();
    }
  };

  useEffect(() => {
    if (!searchOpen) scrollToBottom();
  }, [messages, busy, searchOpen, scrollToBottom]);

  useEffect(() => { knownNameRef.current = knownName; }, [knownName]);

  // Heartbeat: online kal + Koray'dan gelen notları anında al
  useEffect(() => {
    let stopped = false;
    const ping = async () => {
      try {
        const { data } = await axios.post(`${API}/presence/ping`, {
          session_id: sessionId,
          name: knownNameRef.current || null,
          typing: typingRef.current,
        });
        if (!stopped && data?.notes?.length) {
          setMessages((prev) => [
            ...prev,
            ...data.notes.map((n) => ({ id: "koray-" + n.id, role: "koray", content: n.message })),
          ]);
        }
      } catch (e) {
        /* sessiz geç */
      }
    };
    ping();
    const iv = setInterval(ping, 8000);
    return () => { stopped = true; clearInterval(iv); };
  }, [sessionId]);

  const changeMood = (m) => {
    setMood(m);
    localStorage.setItem("serafettin_mood", m);
  };

  const addMsg = (m) => setMessages((prev) => [...prev, m]);
  const errBot = () =>
    addMsg({ id: "err-" + Date.now(), role: "assistant", content: "Bağlantı koptu galiba reis. Az sonra tekrar dene, buralardayım." });

  const sendChat = async (text) => {
    if (!text || busy) return;
    setBusy(looksLikeSearch(text) ? "search" : "chat");
    addMsg({ id: "u-" + Date.now(), role: "user", content: text });
    try {
      const res = await axios.post(`${API}/chat`, { session_id: sessionId, message: text, mood });
      addMsg({ id: "b-" + Date.now(), role: "assistant", content: res.data.reply, searched: res.data.searched });
      maybeSpeak(res.data.reply);
      if (res.data.known_name) setKnownName(res.data.known_name);
    } catch (e) {
      errBot();
    } finally {
      setBusy("");
    }
  };

  const generateImage = async (prompt) => {
    if (!prompt || busy) return;
    setBusy("image");
    addMsg({ id: "u-" + Date.now(), role: "user", content: "🎨 Şunu çizmesini istedim: " + prompt });
    try {
      const res = await axios.post(`${API}/image`, { session_id: sessionId, prompt });
      addMsg({ id: "b-" + Date.now(), role: "assistant", content: res.data.reply, image: res.data.image });
    } catch (e) {
      errBot();
    } finally {
      setBusy("");
    }
  };

  const editImage = async (file, prompt, previewUrl) => {
    if (busy) return;
    setBusy("image");
    addMsg({ id: "u-" + Date.now(), role: "user", content: "🖼️ Şu düzenlemeyi istedim: " + prompt, image: previewUrl });
    try {
      const b64 = await fileToBase64(file);
      const res = await axios.post(`${API}/image`, { session_id: sessionId, prompt, image_base64: b64 });
      addMsg({ id: "b-" + Date.now(), role: "assistant", content: res.data.reply, image: res.data.image });
    } catch (e) {
      errBot();
    } finally {
      setBusy("");
    }
  };

  const runCode = async (prompt) => {
    if (!prompt || busy) return;
    setBusy("code");
    addMsg({ id: "u-" + Date.now(), role: "user", content: "💻 " + prompt });
    try {
      const res = await axios.post(`${API}/code`, { session_id: sessionId, prompt, mood });
      addMsg({ id: "b-" + Date.now(), role: "assistant", content: res.data.reply });
      maybeSpeak(res.data.reply);
    } catch (e) {
      errBot();
    } finally {
      setBusy("");
    }
  };

  const analyzeFile = async (file, prompt, previewUrl) => {
    if (busy) return;
    setBusy("analyze");
    addMsg({
      id: "u-" + Date.now(),
      role: "user",
      content: `📎 '${file.name}' yükledim` + (prompt ? ` — ${prompt}` : ""),
      image: previewUrl || null,
    });
    try {
      const fd = new FormData();
      fd.append("session_id", sessionId);
      fd.append("prompt", prompt || "");
      fd.append("mood", mood);
      fd.append("file", file);
      const res = await axios.post(`${API}/analyze`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      addMsg({ id: "b-" + Date.now(), role: "assistant", content: res.data.reply });
      maybeSpeak(res.data.reply);
    } catch (e) {
      errBot();
    } finally {
      setBusy("");
    }
  };

  const clearChat = async () => {
    try {
      await axios.post(`${API}/clear/${sessionId}`);
    } catch (e) {
      /* yine de temizle */
    }
    setMessages([]);
    setSearchOpen(false);
    setSearchQuery("");
  };

  // --- Sohbet arama ---
  const matches = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    return messages.filter((m) => (m.content || "").toLowerCase().includes(q)).map((m) => m.id);
  }, [searchQuery, messages]);

  const activeMatchId = matches[matchIndex] || null;

  useEffect(() => {
    if (searchOpen && matches.length && activeMatchId) {
      const el = document.querySelector(`[data-mid="${activeMatchId}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [matchIndex, searchQuery, searchOpen, matches.length, activeMatchId]);

  const nextMatch = () => matches.length && setMatchIndex((i) => (i + 1) % matches.length);
  const prevMatch = () => matches.length && setMatchIndex((i) => (i - 1 + matches.length) % matches.length);

  const showWelcome = loaded && messages.length === 0;
  const welcomeChips = knownName
    ? [{ label: "🔙 Kaldığımız yerden devam et", query: "Nerede kalmıştık kanka, kısaca hatırlat da öyle devam edelim" }, ...QUICK_CHIPS]
    : QUICK_CHIPS;

  return (
    <div className="App min-h-screen bg-[#0b141a] text-[#e9edef]">
      <div className="max-w-4xl mx-auto h-screen flex flex-col shadow-2xl border-x border-[#222d34]">
        {/* Header */}
        <header className="h-16 flex items-center justify-between px-3 sm:px-4 bg-[#111b21] border-b border-[#222d34] z-20 shrink-0 gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <OwnerBadge />
            <img
              src={AVATAR}
              alt="Şerafettin"
              className="w-10 h-10 rounded-full object-cover border-2 border-[#00a884] ring-2 ring-[#00a884]/30 shrink-0"
              data-testid="serafettin-avatar"
            />
            <div className="min-w-0">
              <h1 className="font-display text-xl leading-none">ŞERAFETTİN</h1>
              <span className="inline-flex items-center gap-1.5 text-xs text-[#00a884] font-medium truncate" data-testid="serafettin-status">
                <span className="w-1.5 h-1.5 rounded-full bg-[#00a884] inline-block shrink-0" />
                {knownName ? `${knownName}'ı tanıyor · çevrimiçi` : "mahallede, çevrimiçi"}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <MoodSelector mood={mood} onChange={changeMood} />
            <button
              onClick={toggleAutoSpeak}
              data-testid="auto-speak-toggle"
              title={autoSpeak ? "Otomatik seslendirme AÇIK (kapatmak için tıkla)" : "Otomatik seslendirme KAPALI (açmak için tıkla)"}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${autoSpeak ? "bg-[#00a884] text-[#0b141a]" : "text-[#8696a0] hover:text-[#e9edef] hover:bg-[#202c33]"}`}
            >
              {autoSpeak ? <Volume2 size={18} /> : <VolumeX size={18} />}
            </button>
            <button
              onClick={() => setShowVideo(true)}
              data-testid="open-video-button"
              title="Video Stüdyo"
              className="w-10 h-10 rounded-full flex items-center justify-center text-[#8696a0] hover:text-[#00a884] hover:bg-[#202c33] transition-colors"
            >
              <Clapperboard size={18} />
            </button>
            {me?.role === "admin" && (
              <button
                onClick={() => setShowDev(true)}
                data-testid="open-dev-button"
                title="Geliştirici Modu"
                className="w-10 h-10 rounded-full flex items-center justify-center text-[#8696a0] hover:text-[#00a884] hover:bg-[#202c33] transition-colors"
              >
                <Code2 size={18} />
              </button>
            )}
            <button
              onClick={() => { setSearchOpen((o) => !o); setSearchQuery(""); setMatchIndex(0); }}
              data-testid="chat-search-button"
              title="Sohbette ara"
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${searchOpen ? "bg-[#00a884] text-[#0b141a]" : "text-[#8696a0] hover:text-[#e9edef] hover:bg-[#202c33]"}`}
            >
              <Search size={18} />
            </button>
            <button
              onClick={clearChat}
              data-testid="clear-chat-button"
              title="Sohbeti temizle"
              className="w-10 h-10 rounded-full flex items-center justify-center text-[#8696a0] hover:text-[#ef4444] hover:bg-[#202c33] transition-colors"
            >
              <Trash2 size={18} />
            </button>
            {me && FAMILY_GREETINGS[me.name] && (
              <span data-testid="family-badge" className="hidden sm:inline-flex items-center gap-1 text-[10px] font-bold text-[#0b141a] bg-[#f59e0b] rounded-full px-2 py-1 whitespace-nowrap">
                {FAMILY_GREETINGS[me.name].emoji} {FAMILY_GREETINGS[me.name].badge}
              </span>
            )}
            {me ? (
              <button onClick={logout} data-testid="logout-button" title={`${me.name} · çıkış`} className="flex items-center gap-1.5 text-xs font-medium text-[#e9edef] bg-[#202c33] hover:bg-[#2a3942] border border-[#2a3942] rounded-full pl-3 pr-2 py-2 transition-colors">
                <span className="hidden sm:inline max-w-[90px] truncate">{me.name}</span>
                <LogOut size={15} className="text-[#8696a0]" />
              </button>
            ) : (
              <button onClick={() => setShowLogin(true)} data-testid="login-open-button" title="Giriş yap" className="flex items-center gap-1.5 text-xs font-medium text-[#0b141a] bg-[#00a884] hover:bg-[#06cf9c] rounded-full px-3 py-2 transition-colors">
                <LogIn size={15} />
                <span className="hidden sm:inline">Giriş</span>
              </button>
            )}
          </div>
        </header>

        {/* Açılış sloganı — tüm kullanıcılara nasihat */}
        <div
          data-testid="slogan-bar"
          className="flex items-center justify-center gap-2 px-4 py-1.5 bg-[#0a0a0a] border-b border-[#f59e0b]/30 shrink-0"
        >
          <span className="text-[11px] sm:text-xs font-semibold tracking-wide text-[#f59e0b] italic">
            ☝️ Zaman kötü, kolla götü.
          </span>
        </div>

        {/* Arama çubuğu */}
        {searchOpen && (
          <div className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-[#0d1418] border-b border-[#222d34]">
            <Search size={16} className="text-[#8696a0] shrink-0" />
            <input
              autoFocus
              data-testid="chat-search-input"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setMatchIndex(0); }}
              placeholder="Eski konuşmalarda kelime ara..."
              className="flex-1 bg-transparent text-sm text-[#e9edef] placeholder:text-[#8696a0] outline-none"
            />
            <span className="text-xs text-[#8696a0] tabular-nums" data-testid="search-result-count">
              {matches.length ? `${matchIndex + 1}/${matches.length}` : searchQuery ? "0" : ""}
            </span>
            <button onClick={prevMatch} data-testid="search-prev-button" className="p-1.5 rounded hover:bg-[#202c33] text-[#8696a0] disabled:opacity-40" disabled={!matches.length}>
              <ChevronUp size={16} />
            </button>
            <button onClick={nextMatch} data-testid="search-next-button" className="p-1.5 rounded hover:bg-[#202c33] text-[#8696a0] disabled:opacity-40" disabled={!matches.length}>
              <ChevronDown size={16} />
            </button>
          </div>
        )}

        {/* Chat area */}
        <div
          ref={scrollRef}
          data-testid="chat-conversation-container"
          className="chat-bg chat-scroll flex-1 overflow-y-auto p-4 sm:p-6 flex flex-col gap-3"
        >
          {showWelcome && (
            <div
              data-testid="welcome-state"
              className="flex-1 flex flex-col items-center justify-center text-center gap-5 py-8"
            >
              <img
                src={AVATAR}
                alt="Şerafettin"
                className="w-28 h-28 rounded-full object-cover border-4 border-[#00a884] ring-4 ring-[#00a884]/20 shadow-xl"
              />
              <div className="space-y-1.5">
                <span className="inline-block text-xs font-semibold tracking-wide text-[#0b141a] bg-[#f59e0b] px-3 py-1 rounded-full">
                  MAHALLE DELİKANLISI
                </span>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-2 max-w-lg">
                {welcomeChips.map((chip, i) => (
                  <button
                    key={i}
                    data-testid="quick-chip-button"
                    onClick={() => sendChat(chip.query)}
                    disabled={!!busy}
                    className="text-xs font-medium text-[#e9edef] bg-[#202c33] hover:bg-[#2a3942] border border-[#2a3942] hover:border-[#00a884]/60 rounded-full px-4 py-2 transition-all hover:scale-[1.03] active:scale-95 disabled:opacity-50"
                  >
                    {chip.label}
                  </button>
                ))}
              </div>

              {/* Yaratıcı / sahibi kartı */}
              <div
                data-testid="owner-card"
                className="mt-4 flex items-center gap-4 bg-[#111b21] border border-[#f59e0b]/40 rounded-2xl p-4 max-w-md mx-auto text-left shadow-xl"
              >
                <img
                  src={OWNER_IMG}
                  alt="Sahibi"
                  className="w-20 h-20 rounded-full object-cover border-4 border-[#f59e0b] ring-4 ring-[#f59e0b]/20 shrink-0"
                />
                <div className="min-w-0">
                  <div className="inline-block text-[10px] font-bold tracking-wide text-[#0b141a] bg-[#f59e0b] px-2 py-0.5 rounded-full">
                    👑 KORAY · YARATICIM & SAHİBİM
                  </div>
                  <p className="mt-2 text-sm text-[#e9edef] leading-relaxed">
                    "Bu yapay zekânın sahibi Koray'dır. Saygı duyun, önünde eğilin. Benim yaratıcım bu adamdır; mükemmel, yakışıklı ve zeki — adam gibi adam."
                  </p>
                  <p className="mt-1 text-xs text-[#00a884] font-medium">— Şerafettin</p>
                </div>
              </div>
            </div>
          )}

          {messages.map((m) => (
            <MessageBubble key={m.id} m={m} query={searchOpen ? searchQuery : ""} activeMatchId={activeMatchId} onSpeak={speakText} />
          ))}

          {busy === "search" && <BusyIndicator testId="search-in-progress-indicator" icon={Search} label="Sokaklarda bilgi aranıyor (DuckDuckGo)..." />}
          {busy === "stt" && <BusyIndicator testId="stt-in-progress-indicator" icon={Mic} label="Dinliyorum, yazıya çeviriyorum..." />}
          {busy === "chat" && <TypingIndicator />}
          {busy === "image" && <BusyIndicator testId="image-in-progress-indicator" icon={Wand2} label="Şerafettin fırçayı kaptı, çiziyor..." />}
          {busy === "analyze" && <BusyIndicator testId="analyze-in-progress-indicator" icon={FileSearch} label="Dosyayı masaya yatırdı, inceliyor..." />}
          {busy === "code" && <BusyIndicator testId="code-in-progress-indicator" icon={Terminal} label="Kodu yazıp sandbox'ta çalıştırıyor..." />}
        </div>

        {/* Input bar */}
        <ChatInput
          busy={!!busy}
          onSendChat={sendChat}
          onGenerateImage={generateImage}
          onEditImage={editImage}
          onAnalyzeFile={analyzeFile}
          onRunCode={runCode}
          onTyping={(v) => { typingRef.current = v; }}
          onVoice={sendVoice}
        />
      </div>
      {showDev && <DevPanel onClose={() => setShowDev(false)} />}
      {showVideo && <VideoStudio onClose={() => setShowVideo(false)} sessionId={sessionId} />}
      {showLogin && <LoginModal onClose={() => setShowLogin(false)} onLoggedIn={onLoggedIn} />}
    </div>
  );
}

export default App;
